import React from 'react';
import Markdoc from '@markdoc/markdoc';
import {
  MarkdocCtaBand,
  MarkdocCallout,
  MarkdocCard,
  MarkdocCards,
  MarkdocCol,
  MarkdocFaq,
  MarkdocFaqItem,
  MarkdocFeatureGrid,
  MarkdocFeatureItem,
  MarkdocGallery,
  MarkdocHeroSplit,
  MarkdocProcess,
  MarkdocQuote,
  MarkdocRow,
  MarkdocStep,
  MarkdocStat,
  MarkdocStats,
  MarkdocTable,
  MarkdocTimeline,
  MarkdocTimelineItem,
} from './markdoc/MarkdocBlocks';

const SUPPORTED_TAGS = new Set([
  'layoutTable',
  'layoutRow',
  'layoutCol',
  'callout',
  'cards',
  'card',
  'gallery',
  'stats',
  'stat',
  'timeline',
  'timelineItem',
  'faq',
  'faqItem',
  'quote',
  'heroSplit',
  'featureGrid',
  'featureItem',
  'process',
  'step',
  'ctaBand',
]);

function normalizeLegacyLayoutTags(rawContent) {
  if (!rawContent) return '';

  return rawContent
    .replace(/{%\s*table(\s+[^%]*?)?\s*%}/g, '{% layoutTable$1 %}')
    .replace(/{%\s*\/table\s*%}/g, '{% /layoutTable %}')
    .replace(/{%\s*row(\s+[^%]*?)?\s*%}/g, '{% layoutRow$1 %}')
    .replace(/{%\s*\/row\s*%}/g, '{% /layoutRow %}')
    .replace(/{%\s*col(\s+[^%]*?)?\s*%}/g, '{% layoutCol$1 %}')
    .replace(/{%\s*\/col\s*%}/g, '{% /layoutCol %}');
}

const markdocConfig = {
  tags: {
    layoutTable: {
      render: 'MarkdocTable',
      attributes: {
        columns: { type: Number, default: 2 },
      },
    },
    layoutRow: {
      render: 'MarkdocRow',
      attributes: {
        columns: { type: Number, default: 2 },
        reverse: { type: Boolean, default: false },
      },
    },
    layoutCol: {
      render: 'MarkdocCol',
      attributes: {
        span: { type: Number, default: 1 },
      },
    },
    callout: {
      render: 'MarkdocCallout',
      attributes: {
        tone: { type: String, default: 'info' },
        title: { type: String },
      },
    },
    cards: {
      render: 'MarkdocCards',
      attributes: {
        columns: { type: Number, default: 3 },
      },
    },
    card: {
      render: 'MarkdocCard',
      attributes: {
        title: { type: String },
        href: { type: String },
      },
    },
    gallery: {
      render: 'MarkdocGallery',
      attributes: {
        columns: { type: Number, default: 3 },
      },
    },
    stats: {
      render: 'MarkdocStats',
      attributes: {
        columns: { type: Number, default: 3 },
      },
    },
    stat: {
      render: 'MarkdocStat',
      attributes: {
        value: { type: String },
        label: { type: String },
      },
    },
    timeline: {
      render: 'MarkdocTimeline',
    },
    timelineItem: {
      render: 'MarkdocTimelineItem',
      attributes: {
        title: { type: String },
        meta: { type: String },
      },
    },
    faq: {
      render: 'MarkdocFaq',
    },
    faqItem: {
      render: 'MarkdocFaqItem',
      attributes: {
        question: { type: String },
        open: { type: Boolean, default: false },
      },
    },
    quote: {
      render: 'MarkdocQuote',
      attributes: {
        author: { type: String },
        role: { type: String },
      },
    },
    heroSplit: {
      render: 'MarkdocHeroSplit',
      attributes: {
        eyebrow: { type: String },
        title: { type: String },
        subtitle: { type: String },
        image: { type: String },
        imageAlt: { type: String },
        reverse: { type: Boolean, default: false },
        ctaText: { type: String },
        ctaHref: { type: String },
      },
    },
    featureGrid: {
      render: 'MarkdocFeatureGrid',
      attributes: {
        columns: { type: Number, default: 3 },
      },
    },
    featureItem: {
      render: 'MarkdocFeatureItem',
      attributes: {
        title: { type: String },
        icon: { type: String },
      },
    },
    process: {
      render: 'MarkdocProcess',
      attributes: {
        title: { type: String },
      },
    },
    step: {
      render: 'MarkdocStep',
      attributes: {
        title: { type: String },
        number: { type: String },
      },
    },
    ctaBand: {
      render: 'MarkdocCtaBand',
      attributes: {
        title: { type: String },
        buttonText: { type: String },
        buttonHref: { type: String },
        tone: { type: String, default: 'primary' },
      },
    },
  },
};

const markdocComponents = {
  MarkdocTable,
  MarkdocRow,
  MarkdocCol,
  MarkdocCallout,
  MarkdocCards,
  MarkdocCard,
  MarkdocGallery,
  MarkdocStats,
  MarkdocStat,
  MarkdocTimeline,
  MarkdocTimelineItem,
  MarkdocFaq,
  MarkdocFaqItem,
  MarkdocQuote,
  MarkdocHeroSplit,
  MarkdocFeatureGrid,
  MarkdocFeatureItem,
  MarkdocProcess,
  MarkdocStep,
  MarkdocCtaBand,
};

function collectUnsupportedTags(node, unsupported = new Set()) {
  if (!node || typeof node !== 'object') return unsupported;

  if (node.type === 'tag' && node.tag && !SUPPORTED_TAGS.has(node.tag)) {
    unsupported.add(String(node.tag));
  }

  if (Array.isArray(node.children)) {
    node.children.forEach((child) => collectUnsupportedTags(child, unsupported));
  }

  return unsupported;
}

function toDisplayTag(tag) {
  if (tag === 'layoutTable') return 'table';
  if (tag === 'layoutRow') return 'row';
  if (tag === 'layoutCol') return 'col';
  return tag;
}

export default function MarkdocRenderer({ content, className = '' }) {
  if (!content) {
    return null;
  }

  const normalizedContent = normalizeLegacyLayoutTags(content);
  const ast = Markdoc.parse(normalizedContent);
  const unsupportedTags = Array.from(collectUnsupportedTags(ast));
  const transformed = Markdoc.transform(ast, markdocConfig);

  return (
    <div className={`article-markdoc ${className}`.trim()}>
      {unsupportedTags.length > 0 ? (
        <aside className="article-markdoc-callout tone-warning">
          <h4>Unsupported content block detected</h4>
          <div>
            <p>
              This article contains unsupported tag(s):{' '}
              {unsupportedTags.map(toDisplayTag).join(', ')}.
            </p>
            <p>
              Use only supported tags: table, row, col, callout, cards, card, gallery, stats, stat, timeline, timelineItem, faq, faqItem, quote, heroSplit, featureGrid, featureItem, process, step, ctaBand.
            </p>
          </div>
        </aside>
      ) : null}
      {Markdoc.renderers.react(transformed, React, { components: markdocComponents })}
    </div>
  );
}